import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  WelcomeElement,
} from '@components';
import { GlobalContext } from '@contexts';
import { usePageTemplates } from '@hooks';
import { postsQuery, siteQuery } from '@queries';
import { SearchIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';

import PostRow from './PostRow/PostRow.jsx';

const matchPost = (post, query) => {
  // determine filter match for a particular Post

  // explicit matching only
  const exactMatchFields = [post.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const postFields = [post.slug, post.title];
  // location name
  if (postFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

const filterPosts = (posts, query) => {
  // client side filtering of posts
  const cleanedQuery = query.toLowerCase().trim();
  return posts.filter((post) => {
    if (matchPost(post, cleanedQuery)) {
      return post;
    }
  });
};

export const Blogs = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();
  const globals = useContext(GlobalContext);

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  const {
    data: postsData,
    isPending: postsIsPending,
    isError,
  } = useQuery(
    postsQuery({
      limit: 5000,
      order_by: '-date',
      site_id: siteID,
      _fields: ['id', 'title', 'site_id','date', 'published'].join(','),
    }),
  );

  // blog pages to determine if blogging is supported
  const { data: blogPages, isPending: blogPagesIsPending } = usePageTemplates(
    siteID,
    siteData?.theme_id,
    'blog',
  );

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter websites if there is a search term, otherwise return the full list
  const filteredPosts = searchTerm
    ? filterPosts(postsData.objects, searchTerm)
    : postsData?.objects;

  // no blog posts
  const noPosts =
    !postsIsPending && (isError || postsData.objects?.length == 0);

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <span>{t('Blog posts')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Blog posts')}</h1>
              <div className="sb-button-list-inline">
                <Link
                  to={`/dashboard/sites/${siteID}/blogs/posts/new`}
                  className="sb-btn sb-btn-cancel"
                >
                  Add new
                </Link>
              </div>
            </div>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left">
                <div className="sb-flex">
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={postsIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
              <div className="sb-manage-options-right">
                <div className="sb-flex">
                  {globals.visible.siteBlogAuthorsView && (
                    <Link
                      to={`/dashboard/sites/${siteID}/blogs/authors`}
                      className="sb-btn sb-btn-cancel"
                    >
                      Manage authors
                    </Link>
                  )}
                  {globals.visible.siteBlogCategoriesView && (
                    <Link
                      to={`/dashboard/sites/${siteID}/blogs/categories`}
                      className="sb-btn sb-btn-cancel"
                    >
                      Manage categories
                    </Link>
                  )}
                </div>
              </div>
            </div>
            {globals.visible.projectTemplatesView &&
              !blogPagesIsPending &&
              blogPages.length == 0 && (
                <Message
                  messageStyle="warning"
                  title="No blog configured"
                  message="Blogging is currently not supported on this website. A blog page is required to support blogging."
                  fadeoutMessage={false}
                  isFormMessage={false}
                />
              )}
            {postsIsPending && <Loader className="sb-loader sb-loader-large" />}
            {noPosts && (
              <WelcomeElement>
                <p>No blog posts under this website</p>
                <Link
                  to={`/dashboard/sites/${siteID}/blogs/posts/new`}
                  className="sb-btn sb-btn-save"
                >
                  Add first post
                </Link>
              </WelcomeElement>
            )}
            {!postsIsPending && !isError && (
              <div className="sb-content-view-row">
                {filteredPosts.map((post) => {
                  return <PostRow key={post.id} post={post} />;
                })}
              </div>
            )}
            {!postsIsPending && searchTerm && (
              <Message
                messageStyle="notice"
                title={
                  `Filtered to ${filteredPosts.length} of ${postsData.objects.length} post` +
                  (postsData.objects.length != 1 ? 's' : '')
                }
                fadeoutMessage={false}
                isFormMessage={false}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
